import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { FC, ReactElement, MutableRefObject } from 'react'
import {
	Button,
	Flex,
	Input,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	useDisclosure,
	FormControl,
	FormLabel,
	ChakraProps,
	Select,
	PopoverAnchor,
	InputGroup,
	Text,
	Box,
	CheckboxGroup,
	Checkbox,
	Stack,
} from '@chakra-ui/react'
import { useQuery } from 'react-query'
import {
	Controller,
	FieldValues,
	useForm as useHookForm,
} from 'react-hook-form'
import type {
	Control,
	ControllerRenderProps,
	Path,
	ControllerFieldState,
	UseFormStateReturn,
} from 'react-hook-form'
import type { Location as HistoryLocation } from 'history'
import {
	MultiSelectDropdown,
	MultiInputBox,
	FilterIcn,
	ArrowDownIcn,
	StarVerifiedIcon,
} from '@alexanderathoodly/ui-library'
import { servicesQuery } from '../coworking_location/queries'
import { SERVICES } from '../coworking_location/services'
import { useUpdateSerpFilters } from './updateFilters'
import { matchPath, useHistory } from 'react-router-dom'
import type { match } from 'react-router-dom'
import { extractCoworkingSerpParams } from './utils'
import serpRoute from './route'
import { sendGA4Event } from '~/analytics/ga'
import {
	FILTER_TYPE_OFFICE,
	FILTER_TYPE_MEMBERSHIP,
	FILTER_TYPE_BIG_OFFICE,
	FILTER_TYPE_FLEX_OFFICE,
} from './urlGenerator'
import type { FILTER_TYPE_CHOICE } from './urlGenerator'
import { BudgetFilterPopover, BudgetSlider } from './budgetslider'
import { useTranslation } from 'react-i18next'
import { GeoSearchField } from '~/forms/geosearch'
import { useSortSelector } from './utils'
import { InputInfoPopover } from '~/forms/inputinfo'

// Location can come from the window object or from the history library, they have many fields in common but not all
type CommonLocation = Omit<HistoryLocation, 'state'>

type SerpFilterFormValues = {
	areaCodes: string[]
	services: string[]
	maxBudget: number | null
	minWorkspaces: number | null
	maxWorkspaces: number | null
	filterType: FILTER_TYPE_CHOICE[]
}

const getMatch = (location: CommonLocation) =>
	matchPath<{ city: string; neighborhood: string }>(
		location.pathname,
		serpRoute
	)

/**
 * The form for the SERP is not very complex but the sync is a bit complex as it requires sometimes to be out of sync
 * this hook takes care of the sync with the URL as well as the initialization on load for the form and its values
 * it returns the react hook form instance without any change
 */
const useSerpFilterForm = () => {
	const getIdealFormState: (
		match: match<{ city: string; neighborhood: string }>
	) => SerpFilterFormValues = useCallback((match) => {
		const params = new URLSearchParams(location.search)
		const serpParams = extractCoworkingSerpParams(
			params,
			match.params.city,
			match.params.neighborhood,
			location.pathname
		)

		return {
			areaCodes: serpParams.areas,
			services: serpParams.services || [],
			maxBudget: serpParams.budget || null,
			minWorkspaces: serpParams.min_workspaces || null,
			maxWorkspaces: serpParams.max_workspaces || null,
			filterType: serpParams.types || [],
		}
	}, [])
	const history = useHistory()
	const form = useHookForm<SerpFilterFormValues>({
		defaultValues: {
			areaCodes: [],
			services: [],
			maxBudget: null,
			minWorkspaces: null,
			filterType: [],
		},
	})
	const { reset } = form
	// Ideally we'd like to set the default values directly but that would require always be guaranteed to have the business area information
	// So instead we wait to have it (so getIdealFormState gets run again) and reset the form to match the URL
	useEffect(() => {
		const match = getMatch(history.location)
		if (match) {
			reset(getIdealFormState(match), { keepDefaultValues: true })
		}
	}, [history, getIdealFormState, reset])
	// If a navigation is not triggered directly by the form (for instance if the user goes back / forward) we need to manually sync the form
	// with the URL, this is done by listening to the history and calling reset
	useEffect(() => {
		return history.listen((l, a) => {
			const match = getMatch(l)
			if (a === 'POP' && match) {
				reset(getIdealFormState(match), {
					keepDefaultValues: true,
				})
			}
		})
	}, [history, reset, getIdealFormState])
	return form
}

type ControllerFullRenderProps<
	TFieldValues extends FieldValues,
	FieldName extends Path<TFieldValues>
> = {
	field: ControllerRenderProps<TFieldValues, FieldName>
	fieldState: ControllerFieldState
	formState: UseFormStateReturn<TFieldValues>
}

type SpecializedController<
	TFieldValues extends FieldValues,
	FieldName extends Path<TFieldValues>,
	AddRenderProps = unknown,
	P = unknown
> = FC<
	P & {
		control: Control<TFieldValues>
		render: (
			props: ControllerFullRenderProps<TFieldValues, FieldName> & AddRenderProps
		) => ReactElement
	}
>

/**
 * Given a render function and an object with additional properties, return a new
 * render function that includes the additional properties
 */
const useEnhancedRender = <
	TAddProps extends Record<string, unknown>,
	TFieldValues extends FieldValues,
	FieldName extends Path<TFieldValues>
>(
	render: (
		props: ControllerFullRenderProps<TFieldValues, FieldName> & TAddProps
	) => ReactElement,
	addProps: TAddProps
): ((
	props: ControllerFullRenderProps<TFieldValues, FieldName>
) => ReactElement) => {
	return useCallback(
		(props) => {
			return render({ ...props, ...addProps })
		},
		// ESLint does not understand that ...Object.values catches the `addProps` dependency
		// TODO: Find another way to satisfy the dependency?
		// eslint-disable-next-line
		[render, ...Object.values(addProps)]
	)
}

const ServiceFilterController: SpecializedController<
	SerpFilterFormValues,
	'services',
	{ serviceOptions: { value: string; display: string }[] }
> = ({ control, render }) => {
	const { t } = useTranslation()
	const services = useQuery(servicesQuery)
	const serviceOptions = useMemo(() => {
		return services.data
			? services.data
					.filter((service) => service.name in SERVICES)
					.map((service) => {
						return {
							value: service.name,
							display: t(SERVICES[service.name]!.name),
						}
					})
			: []
	}, [services.data, t])
	const enhancedRender = useEnhancedRender(render, { serviceOptions })
	return (
		<Controller name="services" control={control} render={enhancedRender} />
	)
}

const TypeFilterController: SpecializedController<
	SerpFilterFormValues,
	'filterType',
	{ typeOptions: { value: string; display: string }[] }
> = ({ control, render }) => {
	const { t } = useTranslation()
	const typeOptions = [
		{
			value: FILTER_TYPE_MEMBERSHIP,
			display: t('Medlemskap'),
		},
		{
			value: FILTER_TYPE_OFFICE,
			display: t('Kontorsrum'),
		},
		{
			value: FILTER_TYPE_BIG_OFFICE,
			display: t('Big Office'),
		},
		{
			value: FILTER_TYPE_FLEX_OFFICE,
			display: t('Eget kontor'),
		},
	]
	const enhancedRender = useEnhancedRender(render, { typeOptions })
	return (
		<Controller name="filterType" control={control} render={enhancedRender} />
	)
}

const BudgetFilterController: SpecializedController<
	SerpFilterFormValues,
	'maxBudget',
	{
		isOpen: boolean
		onClose: () => void
		onOpen: () => void
		initialFocusRef: MutableRefObject<any>
	}
> = ({ control, render }) => {
	const { isOpen, onClose, onOpen } = useDisclosure()
	const initialFocusRef = useRef()
	const enhancedRender = useEnhancedRender(render, {
		isOpen,
		onClose,
		onOpen,
		initialFocusRef,
	})

	return (
		<Controller name="maxBudget" control={control} render={enhancedRender} />
	)
}

export const FiltersBar: FC<
	{ toggleLayoutButton?: ReactElement; viewMode: 'list' | 'map' } & ChakraProps
> = ({ toggleLayoutButton, viewMode, ...rest }) => {
	const { t } = useTranslation()
	const updateFilters = useUpdateSerpFilters()
	const { control, register, handleSubmit, watch, setValue, getValues } =
		useSerpFilterForm()
	useEffect(() => {
		const subscription = watch(updateFilters)
		return () => subscription.unsubscribe()
	}, [watch, updateFilters])

	const getFontStyle = (fieldName: string) => {
		const hasValueStyle = {
			color: 'blue.500',
			fontWeight: 'semibold',
		}
		const noValueStyle = {
			color: 'grey.800',
		}
		const fieldValue = getValues(fieldName as Path<SerpFilterFormValues>)
		if (Array.isArray(fieldValue)) {
			return fieldValue.length === 0 ? noValueStyle : hasValueStyle
		}
		return fieldValue ? hasValueStyle : noValueStyle
	}

	const getBorderStyle = (fieldName: string) => {
		const hasValueStyle = {
			borderColor: 'blue.500',
		}
		const noValueStyle = {
			borderColor: 'grey.300',
		}
		const fieldValue = getValues(fieldName as Path<SerpFilterFormValues>)
		if (Array.isArray(fieldValue)) {
			return fieldValue.length === 0 ? noValueStyle : hasValueStyle
		}
		return fieldValue ? hasValueStyle : noValueStyle
	}

	return (
		<Flex
			as="form"
			onSubmit={handleSubmit(updateFilters)}
			gap="10px"
			display={{ base: 'none', md: 'flex' }}
			direction="column"
			{...rest}
		>
			<Flex gap="10px" alignItems="end">
				<Box>
					<FormLabel as={Text} htmlFor="minWorkspaces" fontWeight="bold">
						{t('Storlek')}
					</FormLabel>
					<Flex gap="10px" alignItems="center">
						<FormControl width="auto" className="form-control">
							<Input
								placeholder={t('Min')}
								size="md"
								maxW={20}
								type="number"
								{...getBorderStyle('minWorkspaces')}
								{...getFontStyle('minWorkspaces')}
								_placeholder={{
									color: 'grey.800',
									fontWeight: 'normal',
								}}
								{...register('minWorkspaces')}
								onChange={(event) => {
									void register('minWorkspaces').onChange(event)
									sendGA4Event('site_interaction', {
										type: 'filter',
										budget: getValues('maxBudget'),
										workspaces: getValues('minWorkspaces'),
									})
								}}
							/>
						</FormControl>
						<Text>{` - `}</Text>
						<FormControl width="auto" className="form-control">
							<Input
								placeholder={t('Max')}
								size="md"
								maxW={20}
								type="number"
								{...getBorderStyle('maxWorkspaces')}
								{...getFontStyle('maxWorkspaces')}
								_placeholder={{
									color: 'grey.800',
									fontWeight: 'normal',
								}}
								{...register('maxWorkspaces')}
								onChange={(event) => {
									void register('maxWorkspaces').onChange(event)
									sendGA4Event('site_interaction', {
										type: 'filter',
										budget: getValues('maxBudget'),
										workspaces: getValues('minWorkspaces'),
									})
								}}
							/>
						</FormControl>
						<Text>{t('antal arbetsplatser')}</Text>
						<Box
							borderRadius="full"
							height="20px"
							width="20px"
							border="0.5px solid #ECEFF4"
							p="2px"
							ml={2}
						>
							<StarVerifiedIcon height="15px" width="15px" />
						</Box>
						<Text fontSize="xs" color="gray.600">
							{t(
								'Filtrera på antal arbetsplatser för att se vakanser i realtid på kontorsrum, endast på yta.se'
							)}
						</Text>
					</Flex>
				</Box>
				{toggleLayoutButton ? toggleLayoutButton : null}
			</Flex>
			<Flex gap="10px" alignItems="center">
				<BudgetFilterController
					control={control}
					render={({
						field: { value, onChange },
						isOpen,
						onClose,
						onOpen,
						initialFocusRef,
					}) => {
						return (
							<BudgetFilterPopover
								isOpen={isOpen}
								onClose={onClose}
								formValue={value}
								onChange={onChange}
								initialFocusRef={initialFocusRef}
							>
								<PopoverAnchor>
									<FormControl width="auto" className="form-control">
										<FormLabel fontWeight="bold">{t('Hyra')}</FormLabel>
										<Input
											placeholder={t('kr/mån')}
											size="md"
											maxW={28}
											type="number"
											{...getBorderStyle('maxBudget')}
											{...getFontStyle('maxBudget')}
											_placeholder={{ color: 'grey.800', fontWeight: 'normal' }}
											onFocus={onOpen}
											onBlur={onClose}
											ref={initialFocusRef}
											value={value || ''}
											onChange={(event) => {
												onChange(event)
												sendGA4Event('site_interaction', {
													type: 'filter',
													budget: getValues('maxBudget'),
													workspaces: getValues('minWorkspaces'),
												})
											}}
										/>
									</FormControl>
								</PopoverAnchor>
							</BudgetFilterPopover>
						)
					}}
				/>
				<Controller
					control={control}
					name="areaCodes"
					defaultValue={[]}
					render={({ field: { onChange, value } }) => {
						return (
							<FormControl width="auto">
								<FormLabel fontWeight="bold">{t('Område')}</FormLabel>
								<InputGroup mx="auto">
									<GeoSearchField
										onChange={onChange}
										value={value}
										placeholder={t('Skriv stad eller område') || ''}
										{...getBorderStyle('areaCodes')}
										_placeholder={{
											color: 'grey.800',
											fontWeight: 'normal',
										}}
									/>
								</InputGroup>
							</FormControl>
						)
					}}
				/>
				<TypeFilterController
					control={control}
					render={({ typeOptions, field: { value, onChange } }) => (
						<FormControl width="auto" className="form-control">
							<Flex>
								<FormLabel fontWeight="bold">{t('Kontorstyp')}</FormLabel>
								<InputInfoPopover w={3}>
									<OfficeTypeHelpContent py={7} px={3} />
								</InputInfoPopover>
							</Flex>
							<MultiSelectDropdown
								options={typeOptions}
								multiple
								selected={value}
								placeholder={t('Kontorstyp')}
								onChange={onChange}
							/>
						</FormControl>
					)}
				/>
				<ServiceFilterController
					control={control}
					render={({ serviceOptions, field: { value, onChange } }) => (
						<FormControl width="auto" className="form-control">
							<FormLabel fontWeight="bold">{t('Bekvämligheter')}</FormLabel>
							<MultiSelectDropdown
								options={serviceOptions}
								multiple
								selected={value}
								placeholder={t('Bekvämligheter')}
								onChange={onChange}
								onClear={() => setValue('services', [])}
							/>
						</FormControl>
					)}
				/>
				{viewMode === 'list' && <SortField />}
			</Flex>
		</Flex>
	)
}

export const FiltersModalControl: FC<ChakraProps> = ({ ...rest }) => {
	const { t } = useTranslation()
	const updateFilters = useUpdateSerpFilters()
	const { isOpen, onOpen, onClose } = useDisclosure()
	const {
		control,
		register,
		handleSubmit,
		watch,
		formState: { isDirty },
		setValue,
		reset,
		getValues,
	} = useSerpFilterForm()
	useEffect(() => {
		const subscription = watch(updateFilters)
		return () => subscription.unsubscribe()
	}, [watch, updateFilters])
	return (
		<>
			<Button
				leftIcon={
					<FilterIcn
						w={5}
						h={5}
						strokeWidth={2}
						style={{ display: 'inline-block' }}
						color={isDirty ? 'blue.500' : 'black'}
					/>
				}
				iconSpacing="2px"
				onClick={onOpen}
				display={{ base: 'inline-flex', md: 'none' }}
				aria-label="Filtrera"
				variant="link"
				flex={1}
				textAlign="right"
				fontWeight="semibold"
				color="blackAlpha.800"
				textDecoration={isDirty ? 'underline' : 'none'}
				{...rest}
			>
				{t('Sökfilter')}
			</Button>
			<Modal isOpen={isOpen} onClose={onClose} size="full">
				<ModalOverlay />
				<ModalContent as="form" onSubmit={handleSubmit(updateFilters)}>
					<ModalHeader>{t('Filtrera din sökning')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody
						display="flex"
						gap="20px"
						flexDirection="column"
						overflow="scroll"
					>
						<FormControl>
							<FormLabel>{t('Storlek')}</FormLabel>
							<Input
								placeholder={t('Min')}
								size="md"
								type="number"
								{...register('minWorkspaces', {
									setValueAs: (v) => (v ? Number(v) : null),
								})}
							/>
						</FormControl>
						<FormControl>
							<Input
								placeholder={t('Max')}
								size="md"
								type="number"
								{...register('maxWorkspaces', {
									setValueAs: (v) => (v ? Number(v) : null),
								})}
							/>
						</FormControl>
						<BudgetFilterController
							control={control}
							render={({ field: { value, onChange } }) => {
								return (
									<>
										<FormControl>
											<FormLabel>{t('Budget / mån.')}</FormLabel>
											<Input
												placeholder={t('Budget / mån')}
												size="md"
												type="number"
												{...register('maxBudget')}
												value={value || undefined}
												onChange={(event) => {
													void register('maxBudget').onChange(event)
													sendGA4Event('site_interaction', {
														type: 'filter',
														budget: getValues('maxBudget'),
														workspaces: getValues('minWorkspaces'),
													})
												}}
											/>
										</FormControl>
										<BudgetSlider formValue={value} onChange={onChange} />
									</>
								)
							}}
						/>
						<Controller
							control={control}
							name="filterType"
							defaultValue={[]}
							render={({ field: { onChange, value } }) => {
								return (
									<FormControl width="auto" className="form-control">
										<Flex>
											<FormLabel>{t('Kontorstyp')}</FormLabel>
											<InputInfoPopover w={3}>
												<OfficeTypeHelpContent py={5} px={3} />
											</InputInfoPopover>
										</Flex>
										<CheckboxGroup
											value={value}
											colorScheme="blue"
											onChange={onChange}
										>
											<Stack spacing={1}>
												<Checkbox value={FILTER_TYPE_MEMBERSHIP}>
													{t('Medlemskap')}
												</Checkbox>
												<Checkbox value={FILTER_TYPE_OFFICE}>
													{t('Kontorsrum')}
												</Checkbox>
												<Checkbox value={FILTER_TYPE_BIG_OFFICE}>
													{t('Big Office')}
												</Checkbox>
												<Checkbox value={FILTER_TYPE_FLEX_OFFICE}>
													{t('Eget kontor')}
												</Checkbox>
											</Stack>
										</CheckboxGroup>
									</FormControl>
								)
							}}
						/>
						<Controller
							control={control}
							name="areaCodes"
							defaultValue={[]}
							render={({ field: { onChange, value } }) => {
								return (
									<FormControl>
										<FormLabel>{t('Område')}</FormLabel>
										<InputGroup>
											<GeoSearchField
												onChange={onChange}
												value={value}
												placeholder={t('Skriv stad eller område') || ''}
											/>
										</InputGroup>
									</FormControl>
								)
							}}
						/>
						<ServiceFilterController
							control={control}
							render={({ serviceOptions, field: { value, onChange } }) => (
								<FormControl>
									<FormLabel>{t('Bekvämligheter')}</FormLabel>
									<MultiInputBox
										options={serviceOptions}
										multiple
										selected={value}
										onChange={onChange}
										onClear={() => setValue('services', [])}
									/>
								</FormControl>
							)}
						/>
					</ModalBody>
					<ModalFooter
						display="flex"
						position="sticky"
						gap="12px"
						bottom={0}
						backgroundColor="white"
					>
						<Button
							variant="outline"
							flex="1"
							onClick={() => {
								reset()
							}}
						>
							{t('Rensa Filter')}
						</Button>
						<Button
							onClick={() => {
								onClose()
							}}
							type="submit"
							flex="1"
						>
							{t('Visa resultat')}
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	)
}

export const SortField: FC<ChakraProps> = ({ ...rest }) => {
	const { t } = useTranslation()
	const { sortMode, onChangeSortSelector, sortOptions } = useSortSelector()
	return (
		<FormControl width="auto" className="form-control" {...rest}>
			<FormLabel fontWeight={{ md: 'bold' }}>{t('Sortering')}</FormLabel>
			<Select
				value={sortMode}
				onChange={(e) => onChangeSortSelector(e.target.value)}
				icon={<ArrowDownIcn strokeWidth="3.5" />}
				iconSize="10px"
				borderColor="grey.300"
			>
				{sortOptions.map((option) => (
					<option key={option.value} value={option.value}>
						{option.text}
					</option>
				))}
			</Select>
		</FormControl>
	)
}

export const OfficeTypeHelpContent: FC<ChakraProps> = ({ ...rest }) => {
	const { t } = useTranslation()
	return (
		<Flex direction="column" gap={3} {...rest}>
			<Box>
				<Text fontWeight="semibold">{t('Medlemskap')}</Text>
				<Text>
					{t(
						'Medlemskap som ger tillgång till gemensamma arbets-, lounge och mötesytor.'
					)}
				</Text>
			</Box>
			<Box>
				<Text fontWeight="semibold">{t('Kontorsrum')}</Text>
				<Text>
					{t(
						'Privat kontorsrum med tillgång till gemensamma ytor och mötesrum.'
					)}
				</Text>
			</Box>
			<Box>
				<Text fontWeight="semibold">{t('Big Office')}</Text>
				<Text>
					{t(
						'Ett eget kontor inom coworking - ofta med egna mötes- och loungeytor samt tillgång till det gemensamma.'
					)}
				</Text>
			</Box>
			<Box>
				<Text fontWeight="semibold">{t('Eget kontor')}</Text>
				<Text>
					{t(
						'Egna kontor med kortare avtal samt möjlighet till möbler, drift och service.'
					)}
				</Text>
			</Box>
		</Flex>
	)
}

// const SerpFilterTag: FC<{
// 	value: string
// 	onRemove?: () => void
// }> = ({ value, onRemove }) => {
// 	return (
// 		<Box
// 			borderRadius="full"
// 			backgroundColor="blue.50"
// 			color="blue.500"
// 			fontSize="xs"
// 			p="2px 8px"
// 			mr={2}
// 			mb={2}
// 		>
// 			{value}
// 			<Box
// 				as="span"
// 				onClick={onRemove}
// 				cursor="pointer"
// 				ml={2}
// 				color="blue.500"
// 				_hover={{ color: 'blue.700' }}
// 			>
// 				x
// 			</Box>
// 		</Box>
// 	)
// }

// const SerpWorkspacesTags: FC = () => {
// 	const { setValue, getValues, watch, handleSubmit, control, register } =
// 		useSerpFilterForm()
// 	const updateFilters = useUpdateSerpFilters()

// 	const minWs = watch('minWorkspaces')
// 	const maxWs = watch('maxWorkspaces')
// 	useEffect(() => {
// 		const subscription = watch(updateFilters)

// 		return () => subscription.unsubscribe()
// 	}, [watch, updateFilters])

// 	return (
// 		<Flex as="form" onSubmit={handleSubmit(updateFilters)}>
// 			{/* {minWs && maxWs ? (
// 				<Controller
// 					control={control}
// 					{...register('minWorkspaces')}
// 					render={({ field: { onChange, value } }) => {
// 						return (
// 							<SerpFilterTag
// 								value={`${minWs} - ${maxWs} arbetsplatser`}
// 								onRemove={() => {
// 									onChange(null)
// 								}}
// 							/>
// 						)
// 					}}
// 				/>
// 			) : null} */}

// 			{minWs ? (
// 				<Controller
// 					control={control}
// 					{...register('minWorkspaces')}
// 					render={({ field: { onChange, value } }) => {
// 						return (
// 							<SerpFilterTag
// 								value={`Min ${minWs} arbetsplatser`}
// 								onRemove={() => {
// 									setValue('minWorkspaces', null)
// 								}}
// 							/>
// 						)
// 					}}
// 				/>
// 			) : null}
// 			{maxWs ? (
// 				<Controller
// 					control={control}
// 					{...register('maxWorkspaces')}
// 					render={({ field: { onChange, value } }) => {
// 						return (
// 							<SerpFilterTag
// 								value={`Max ${maxWs} arbetsplatser`}
// 								onRemove={() => {
// 									onChange(null)
// 								}}
// 							/>
// 						)
// 					}}
// 				/>
// 			) : null}
// 		</Flex>
// 	)
// }

// export const SerpFiltersTags: FC = () => {
// 	return (
// 		<Flex>
// 			<SerpWorkspacesTags />
// 		</Flex>
// 	)
// }
